import { collection, doc, setDoc, updateDoc, getDocs, query, where, orderBy, limit, serverTimestamp, runTransaction, CollectionReference, DocumentData } from 'firebase/firestore';
import { db } from '../../../firebase';
import { TaskScope, TaskStatus, TaskActionType, KanbanTask, AuditLogEntry } from '@/types';

export function getTaskCollectionRef(scope: TaskScope, uid: string, householdId: string | null): CollectionReference<DocumentData> {
  if (scope === 'personal' || householdId === null) {
    return collection(db, 'users', uid, 'personalTasks');
  } else {
    return collection(db, 'households', householdId, 'tasks');
  }
}

export async function createTask(taskData: Omit<KanbanTask, 'id' | 'createdAt' | 'updatedAt'>, uid: string, householdId: string | null): Promise<KanbanTask> {
  const collectionRef = getTaskCollectionRef(taskData.scope, uid, householdId);
  // Use pre-generated ID from caller if provided (enables optimistic UI), otherwise generate one
  const newId = (taskData as any).id || doc(collectionRef).id;

  const taskWithTimestamps = {
    ...taskData,
    id: newId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const logCollectionPath = taskData.scope === 'household' && householdId
    ? `householdTaskLogs`
    : `users/${uid}/audit_logs`;

  const auditLogEntry: AuditLogEntry = {
    id: crypto.randomUUID(),
    actorId: uid,
    actionType: 'CREATE',
    targetId: newId,
    taskTitle: taskData.title,
    scope: taskData.scope,
    timestamp: Date.now(),
    syncType: 'realtime'
  };

  await Promise.all([
    setDoc(doc(collectionRef, newId), taskWithTimestamps),
    setDoc(doc(db, logCollectionPath, auditLogEntry.id),
      taskData.scope === 'household' && householdId ? { ...auditLogEntry, householdId } : auditLogEntry)
  ]);

  return {
    ...taskData,
    id: newId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  } as KanbanTask;
}

export async function updateTaskStatus(taskId: string, newStatus: TaskStatus, actorId: string, task: KanbanTask, householdId: string | null): Promise<void> {
  const taskRef = doc(getTaskCollectionRef(task.scope, actorId, householdId), taskId);

  const updates: Record<string, unknown> = {
    status: newStatus,
    updatedAt: serverTimestamp(),
    lastModifiedBy: actorId
  };

  if (newStatus === 'done' || newStatus === 'finalized') {
    updates.completedAt = serverTimestamp();
  }

  const auditLogEntry: AuditLogEntry = {
    id: crypto.randomUUID(),
    actorId,
    actionType: 'MOVE',
    targetId: taskId,
    taskTitle: task.title,
    scope: task.scope,
    fromStatus: task.status,
    toStatus: newStatus,
    timestamp: Date.now(),
    syncType: 'realtime'
  };

  const logCollectionPath = task.scope === 'household' && householdId
    ? `householdTaskLogs`
    : `users/${actorId}/audit_logs`;

  await Promise.all([
    updateDoc(taskRef, updates),
    setDoc(doc(db, logCollectionPath, auditLogEntry.id),
      task.scope === 'household' && householdId ? { ...auditLogEntry, householdId } : auditLogEntry)
  ]);

  if (newStatus === 'done' && task.scope === 'household' && householdId) {
    await runTransaction(db, async (transaction) => {
      const householdRef = doc(db, 'households', householdId);
      const householdDoc = await transaction.get(householdRef);

      if (householdDoc.exists()) {
        const householdData = householdDoc.data();
        const members = householdData.members || [];
        const updatedMembers = members.map((member: Record<string, unknown>) => {
          if (member.userId === actorId) {
            return {
              ...member,
              contributionPoints: ((member.contributionPoints as number) || 0) + (task.contributionPoints ?? 10)
            };
          }
          return member;
        });

        transaction.update(householdRef, { members: updatedMembers });
      }
    });
  }
}

export async function deleteTask(taskId: string, task: KanbanTask, actorId: string, householdId: string | null): Promise<void> {
  const taskRef = doc(getTaskCollectionRef(task.scope, actorId, householdId), taskId);

  const auditLogEntry: AuditLogEntry = {
    id: crypto.randomUUID(),
    actorId,
    actionType: 'DELETE',
    targetId: taskId,
    taskTitle: task.title,
    scope: task.scope,
    timestamp: Date.now(),
    syncType: 'realtime'
  };

  const logCollectionPath = task.scope === 'household' && householdId
    ? `householdTaskLogs`
    : `users/${actorId}/audit_logs`;

  await Promise.all([
    updateDoc(taskRef, {
      isDeleted: true,
      updatedAt: serverTimestamp(),
      lastModifiedBy: actorId
    }),
    setDoc(doc(db, logCollectionPath, auditLogEntry.id),
      task.scope === 'household' && householdId ? { ...auditLogEntry, householdId } : auditLogEntry)
  ]);
}

export async function fetchAuditLogs(householdId: string, filters?: {
  userId?: string;
  actionType?: TaskActionType;
  startDate?: Date;
  endDate?: Date;
}): Promise<AuditLogEntry[]> {
  let q = query(
    collection(db, 'householdTaskLogs'),
    where('householdId', '==', householdId),
    orderBy('timestamp', 'desc'),
    limit(100)
  );

  if (filters?.userId) {
    q = query(q, where('actorId', '==', filters.userId));
  }
  if (filters?.actionType) {
    q = query(q, where('actionType', '==', filters.actionType));
  }
  if (filters?.startDate) {
    q = query(q, where('timestamp', '>=', filters.startDate.getTime()));
  }
  if (filters?.endDate) {
    q = query(q, where('timestamp', '<=', filters.endDate.getTime()));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      ...data,
      timestamp: typeof data.timestamp === 'number' ? data.timestamp : (data.timestamp as any)?.toMillis?.() ?? data.timestamp
    } as AuditLogEntry;
  });
}
